<template>
  <div class="hotel-price-rule-view">
    <StandardSearchPageView
      ref="table"
      rowKey="id"
      :col="3"
      :columns="tableColumns"
      :queryParams="queryParams"
      :searchOptions="searchOptions"
      :operationBoxStyle="{ padding: '0 0 16px' }"
      :requestPromise="loadDataList"
    >
      <a-button slot="operation" type="primary" icon="plus" @click="onAddEvent">
        新建价格规则
      </a-button>
      <template v-slot:action="{ record }">
        <a-space>
          <a @click="onEditEvent(record)">编辑</a>
          <a-popconfirm
            :title="Number(record.status) === 1 ? '确认禁用该价格规则？' : '确认启用该价格规则？'"
            @confirm="onSwitchStatus(record, Number(record.status) === 1 ? 0 : 1)"
          >
            <a>{{ Number(record.status) === 1 ? '禁用' : '启用' }}</a>
          </a-popconfirm>
        </a-space>
      </template>
    </StandardSearchPageView>

    <SimpleFormEditorDialog
      v-if="showDialog"
      ref="formEditor"
      :editConfigList="currentEditConfigList"
      :item="selectItem"
      :rules="rules"
      :title="selectItem && selectItem.id ? '编辑价格规则' : '新建价格规则'"
      :width="720"
      @cancel="showDialog = false"
      @ok="onEditResultEvent"
    >
      <template v-slot:accountId="{ form }">
        <a-select
          v-model="form.accountId"
          :options="accountOptions"
          allowClear
          placeholder="请选择石基账号"
          style="width: 100%"
          @change="onAccountChange(form)"
        />
      </template>
      <template v-slot:hotelId="{ form }">
        <a-select
          v-model="form.hotelId"
          :options="hotelOptions"
          allowClear
          show-search
          option-filter-prop="label"
          placeholder="请选择酒店名称"
          style="width: 100%"
          @change="(value) => onHotelChange(form, value)"
        />
      </template>
      <template v-slot:dateRange="{ form }">
        <a-range-picker
          v-model="form.dateRange"
          style="width: 100%"
          valueFormat="YYYY-MM-DD"
          format="YYYY-MM-DD"
        />
      </template>
      <template v-slot:calcType="{ form }">
        <a-select
          v-model="form.calcType"
          :options="calcTypeOptions"
          allowClear
          placeholder="请选择计算方式"
          style="width: 100%"
          @change="() => onCalcTypeChange(form)"
        />
      </template>
      <template v-slot:adjustValue="{ form }">
        <a-input-number
          v-model="form.adjustValue"
          :precision="2"
          :min="Number(form.calcType) === 2 ? 0 : undefined"
          :max="Number(form.calcType) === 2 ? 100 : undefined"
          :placeholder="Number(form.calcType) === 2 ? '请输入销售价比例，0-100' : '请输入调整金额，允许负数'"
          style="width: 100%"
        />
        <div v-if="Number(form.calcType) === 2" class="field-tip">单位：百分比，范围 0-100，例如 98 表示结算价的 98%</div>
        <div v-else-if="Number(form.calcType) === 1" class="field-tip">单位：元，固定金额加减，允许负数</div>
      </template>
    </SimpleFormEditorDialog>
  </div>
</template>

<script>
import SimpleFormEditorDialog from '@/customComponents/SimpleFormEditorDialog.vue'
import StandardSearchPageView from '@/customComponents/standard/StandardSearchPageView.vue'
import {
  CALC_TYPE_OPTIONS,
  editConfigList,
  searchOptions,
  tableColumns
} from '../config/hotelPriceRule.config.js'

function formatDateRange (startDate, endDate) {
  if (!startDate && !endDate) return ''
  return `${startDate || '-'} ~ ${endDate || '-'}`
}

function formatAdjustValue (item = {}) {
  const calcType = item.calculationType != null ? item.calculationType : item.calcType
  if (Number(calcType) === 2) {
    const rate = item.priceRate != null ? item.priceRate : item.adjustValue
    if (rate === undefined || rate === null || rate === '') return ''
    return `${rate}%`
  }
  const amount = item.adjustmentAmount != null ? item.adjustmentAmount : item.adjustValue
  if (amount === undefined || amount === null || amount === '') return ''
  return amount
}

function getCalcTypeName (calcType) {
  const matched = CALC_TYPE_OPTIONS.find(item => item.value === calcType)
  return matched ? matched.label : ''
}

export default {
  name: 'HotelPriceRuleComponent',
  components: {
    SimpleFormEditorDialog,
    StandardSearchPageView
  },
  data () {
    return {
      showDialog: false,
      selectItem: undefined,
      queryParams: {},
      accountOptions: [],
      hotelOptions: [],
      searchOptions,
      tableColumns,
      editConfigList,
      calcTypeOptions: CALC_TYPE_OPTIONS,
      rules: {
        accountId: [{ required: true, message: '请选择石基账号' }],
        ruleName: [{ required: true, message: '请输入规则名称' }],
        hotelId: [{ required: true, message: '请选择酒店名称' }],
        dateRange: [{ required: true, message: '请选择生效日期' }],
        calcType: [{ required: true, message: '请选择计算方式' }],
        adjustValue: [
          { required: true, message: '请输入调整值' },
          {
            validator: (rule, value, callback) => {
              const calcType = this.$refs.formEditor &&
                this.$refs.formEditor.form &&
                this.$refs.formEditor.form.calcType
              if (Number(calcType) === 2 && value != null && (Number(value) < 0 || Number(value) > 100)) {
                callback(new Error('销售价比例需在 0-100 之间'))
                return
              }
              callback()
            }
          }
        ]
      }
    }
  },
  computed: {
    currentEditConfigList () {
      const calcType = this.selectItem && this.selectItem.calcType
      return this.editConfigList.map(item => {
        if (item.key !== 'adjustValue') return item
        if (Number(calcType) === 2) {
          return {
            ...item,
            name: '销售价比例（%）',
            placeholder: '请输入销售价比例'
          }
        }
        return {
          ...item,
          name: '调整金额（元）',
          placeholder: '请输入调整金额（元）'
        }
      })
    }
  },
  mounted () {
    this.loadAccountOptions()
  },
  methods: {
    getSourceService () {
      return this.getHttpServices().getSourceService()
    },
    loadAccountOptions () {
      const params = {
        pageNum: 1,
        pageSize: 200,
        merchantId: this.$store.getters.userInfo.merchantId
      }
      this.getSourceService()
        .post('shiji/platform/account_list', params)
        .then(res => {
          const list = (res && res.list) || (res && res.data) || (Array.isArray(res) ? res : [])
          this.accountOptions = list.map(item => ({
            label: item.channelUserName || item.accountName || String(item.id),
            value: item.id
          }))
        })
        .catch(err => {
          console.error(err)
        })
    },
    loadHotelOptions (accountId) {
      if (!accountId) {
        this.hotelOptions = []
        return Promise.resolve()
      }
      const params = {
        pageNum: 1,
        pageSize: 500,
        merchantId: this.$store.getters.userInfo.merchantId
      }
      return this.getSourceService()
        .post('shiji/platform/productUpdate_list', params)
        .then(res => {
          const page = res || {}
          const list = page.data || page.list || []
          this.hotelOptions = list.map(item => {
            const proId = item.productId
            const hotelName = item.productInfo || String(proId || '')
            return {
              label: hotelName,
              value: proId,
              proId,
              hotelCode: item.shijiHotelCode,
              hotelName
            }
          }).filter(item => item.value != null)
        })
        .catch(err => {
          this.hotelOptions = []
          console.error(err)
        })
    },
    onAccountChange (form) {
      this.$set(form, 'hotelId', undefined)
      this.$set(form, 'hotelName', undefined)
      this.loadHotelOptions(form.accountId)
    },
    onHotelChange (form, value) {
      const matched = this.hotelOptions.find(item => item.value === value)
      this.$set(form, 'hotelName', matched ? matched.label : undefined)
    },
    onCalcTypeChange (form) {
      this.$set(form, 'adjustValue', undefined)
      if (this.selectItem) {
        this.$set(this.selectItem, 'calcType', form.calcType)
      }
    },
    normalizeListItem (item = {}) {
      const calcType = item.calculationType != null ? item.calculationType : item.calcType
      const adjustValue = Number(calcType) === 2
        ? item.priceRate
        : (item.adjustmentAmount != null ? item.adjustmentAmount : item.adjustValue)
      return {
        ...item,
        accountId: item.shijiAccountId != null ? item.shijiAccountId : item.accountId,
        calcType,
        adjustValue,
        dateRangeText: formatDateRange(item.startDate, item.endDate),
        calcTypeName: item.calculationTypeName || getCalcTypeName(calcType),
        adjustValueText: formatAdjustValue(item),
        statusName: item.statusName || (Number(item.status) === 1 ? '启用' : '禁用'),
        accountName: item.accountName || item.channelUserName
      }
    },
    buildFormItem (item) {
      if (!item) {
        return {
          accountId: undefined,
          ruleName: undefined,
          hotelId: undefined,
          hotelName: undefined,
          dateRange: [],
          calcType: undefined,
          adjustValue: undefined
        }
      }
      const calcType = item.calculationType != null ? item.calculationType : item.calcType
      return {
        ...item,
        accountId: item.shijiAccountId != null ? item.shijiAccountId : item.accountId,
        hotelId: item.proId != null ? item.proId : item.hotelId,
        calcType,
        adjustValue: Number(calcType) === 2
          ? item.priceRate
          : (item.adjustmentAmount != null ? item.adjustmentAmount : item.adjustValue),
        dateRange: item.startDate && item.endDate ? [item.startDate, item.endDate] : (item.dateRange || [])
      }
    },
    buildListParams (params = {}) {
      const { limitDto = {}, hotelName, effectiveDate, status, shijiAccountId } = params
      const requestParams = {
        merchantId: this.$store.getters.userInfo.merchantId,
        pageNum: limitDto.pageNum || 1,
        pageSize: limitDto.pageSize || 20
      }
      // 有值才放进请求体，避免把空值传给后端。
      if (hotelName) requestParams.hotelName = hotelName
      if (effectiveDate) requestParams.effectiveDate = effectiveDate
      if (status === 0 || status === 1 || status === '0' || status === '1') {
        requestParams.status = Number(status)
      }
      if (shijiAccountId) requestParams.shijiAccountId = shijiAccountId
      return requestParams
    },
    buildSaveParams (params = {}) {
      const dateRange = params.dateRange || []
      const calculationType = Number(params.calcType)
      const saveParams = {
        merchantId: this.$store.getters.userInfo.merchantId,
        shijiAccountId: params.accountId,
        proId: params.hotelId,
        ruleName: params.ruleName,
        startDate: dateRange[0],
        endDate: dateRange[1],
        calculationType,
        adjustmentAmount: null,
        priceRate: null
      }
      if (calculationType === 1) {
        saveParams.adjustmentAmount = params.adjustValue
        saveParams.priceRate = params.priceRate != null ? params.priceRate : null
      } else if (calculationType === 2) {
        saveParams.priceRate = params.adjustValue
        saveParams.adjustmentAmount = params.adjustmentAmount != null ? params.adjustmentAmount : null
      }
      if (params.id != null) {
        saveParams.id = params.id
        saveParams.version = params.version
      }
      return saveParams
    },
    loadDataList (params) {
      return this.getSourceService()
        .post('shiji/platform/price_rule_list', this.buildListParams(params))
        .then(res => {
          const page = res || {}
          const list = page.data || page.list || []
          return {
            data: list.map(this.normalizeListItem),
            totalCount: page.totalCount || page.total || 0,
            pageCount: page.pageCount || page.pages || 1
          }
        })
    },
    onAddEvent () {
      this.hotelOptions = []
      this.selectItem = this.buildFormItem()
      this.showDialog = true
    },
    onEditEvent (record) {
      this.selectItem = this.buildFormItem(record)
      this.showDialog = true
      this.loadHotelOptions(record.accountId || record.shijiAccountId)
    },
    onSwitchStatus (record, status) {
      if (!record || record.id == null) {
        this.$message.error('规则ID缺失')
        return
      }
      if (record.version == null) {
        this.$message.error('规则版本号缺失，请刷新列表后重试')
        return
      }
      if (String(record.status) === String(status)) {
        this.$message.info(status === 1 ? '当前已是启用状态' : '当前已是禁用状态')
        return
      }
      const params = {
        merchantId: this.$store.getters.userInfo.merchantId,
        id: record.id,
        status: Number(status),
        version: record.version
      }
      this.getSourceService()
        .post('shiji/platform/price_rule_status_update', params)
        .then(() => {
          this.$message.success(status === 1 ? '已启用' : '已禁用')
          this.refreshData(false)
        })
        .catch(err => {
          this.$message.error((err && err.msg) || '操作失败，请重试！')
          console.error(err)
        })
    },
    refreshData (resetPage) {
      if (this.$refs.table) {
        this.$refs.table.refresh(resetPage)
      }
    },
    onEditResultEvent (params) {
      const requestParams = this.buildSaveParams(params)
      if (!requestParams.shijiAccountId) {
        this.$message.warning('请选择石基账号')
        return
      }
      if (!requestParams.proId) {
        this.$message.warning('请选择酒店名称')
        return
      }
      if (!requestParams.startDate || !requestParams.endDate) {
        this.$message.warning('请选择生效日期')
        return
      }
      if (requestParams.calculationType === 1 && (requestParams.adjustmentAmount == null || requestParams.adjustmentAmount === '')) {
        this.$message.warning('请输入调整金额')
        return
      }
      if (requestParams.calculationType === 2 && (requestParams.priceRate == null || requestParams.priceRate === '')) {
        this.$message.warning('请输入销售价比例')
        return
      }
      if (requestParams.calculationType === 2 && (Number(requestParams.priceRate) < 0 || Number(requestParams.priceRate) > 100)) {
        this.$message.warning('销售价比例需在 0-100 之间')
        return
      }
      const isEdit = !!params.id
      if (isEdit && requestParams.version == null) {
        this.$message.error('规则版本号缺失，请刷新列表后重试')
        return
      }
      const url = isEdit
        ? 'shiji/platform/price_rule_edit'
        : 'shiji/platform/price_rule_create'
      this.getSourceService()
        .post(url, requestParams)
        .then(() => {
          this.showDialog = false
          this.selectItem = undefined
          this.$message.success('操作成功！')
          this.refreshData(!isEdit)
        })
        .catch(err => {
          this.$message.error((err && err.msg) || '操作失败，请重试！')
          console.error(err)
        })
    }
  }
}
</script>

<style scoped>
.hotel-price-rule-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.field-tip {
  margin-top: 4px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
  line-height: 1.4;
}
</style>
